/* eslint-disable no-undef */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { useContext, useState } from 'react';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import { styled } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { GlobalContext } from 'providers/GlobalProvider';
import { canScheduleTicket } from 'utils/reScreening';
import Checkbox from '@mui/material/Checkbox';
import OutlinedInput from '@mui/material/OutlinedInput';

interface ILoginWizard {
  cur_user: any;
  ticketType?: string;
  // The docType of the ticket being created. Only ticket types that run on a
  // schedule (see utils/reScreening canScheduleTicket) get the frequency step —
  // one-off types (Unified workflows, Classification, ECCN, EAR, etc.) never
  // re-run, so offering a recurring cadence there is misleading.
  docType?: string;
  tariffJurisdiction?: string;
  setFrequency?: any;
  frequency?: string;
  dateStrategy?: 'current' | 'original';
  setDateStrategy?: (strategy: 'current' | 'original') => void;
  handleSaveTicket?: any;
  newTicketName?: string;
  setNewTicketName?: any;
  shareEmail: string[];
  setShareEmail?: any;
  savingTicket: boolean;
}

const CustomSelect = styled(Select)({
  '.MuiSelect-icon': {
    color: 'var(--card-text)',
  },
  overflow: 'hidden',
  color: 'var(--Text-Main, var(--text-primary))',
  textOverflow: 'ellipsis',
  fontFamily: 'Inter',
  fontSize: '16px',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: '24px',
  borderRadius: '8px',
  border: '1px solid var(--bg-secondary)',
});

const SELECT_ALL_VALUE = '__all__';

const CreateTicketWizard: React.FC<ILoginWizard> = ({
  cur_user,
  ticketType,
  docType,
  tariffJurisdiction,
  setFrequency,
  frequency,
  dateStrategy = 'current',
  setDateStrategy,
  handleSaveTicket,
  newTicketName,
  setNewTicketName,
  shareEmail,
  setShareEmail,
  savingTicket,
}) => {
  const [step, setStep] = useState<number>(1);
  const [teamOpt, setTeamOpt] = useState<string>('no');
  //   const [ newTicketName, setNewTicketName ] = useState<string>('');
  const [acctCreating] = useState<boolean>(false);
  //   const [ shareEmail, setShareEmail ] = useState('');
  const { userOpts } = useContext(GlobalContext);

  const filteredUserOpts = userOpts.filter(
    (person) => person.label !== cur_user.basics.email,
  );
  const availableUsers = filteredUserOpts.map((person) => person.label);
  const allSelected =
    availableUsers.length > 0 && shareEmail.length === availableUsers.length;

  // --- Logic for Flows ---
  const isScreening = ticketType === 'screening';
  const isTariff = ticketType === 'tariff';
  // The frequency step is shown only for ticket types that actually run on a
  // schedule (see utils/reScreening). ticketType alone is too coarse — e.g.
  // the EAR flow passes ticketType='screening' but creates a non-re-screenable
  // 'Export Unified' ticket — so gate on the concrete docType being created.
  const needsFrequencyStep = canScheduleTicket(docType, tariffJurisdiction);
  const currentFrequency = frequency ?? 'Once';

  // Requirement: Display Tariff selection if dates are today AND frequency is more than once
  const shouldShowDateRefreshStep =
    isTariff && needsFrequencyStep && currentFrequency !== 'Once';

  // --- Dynamic Step Calculation ---
  const frequencyStepNumber = teamOpt === 'yes' ? 4 : 3;
  const dateRefreshStepNumber = frequencyStepNumber + 1;

  // Calculate Total Steps based on current path
  let totalSteps = 2; // Starts with Name + Share
  if (teamOpt === 'yes') totalSteps += 1;
  if (needsFrequencyStep) totalSteps += 1;
  if (shouldShowDateRefreshStep) totalSteps += 1;

  const handleStep = (action: string) => {
    if (action === 'increase') {
      setStep(Math.min(step + 1, totalSteps));
    }
    if (action === 'decrease') {
      setStep(Math.max(step - 1, 1));
    }
  };

  const handleTeamYesSelect = () => {
    setTeamOpt('yes');
  };

  const handleTeamNoSelect = () => {
    setTeamOpt('no');
  };

  // Find the handleShareChange function (around line 116) and update it:

  const handleShareChange = (e: any) => {
    const value =
      typeof e.target.value === 'string'
        ? e.target.value.split(',')
        : e.target.value;

    if (value.includes(SELECT_ALL_VALUE)) {
      // Check if selecting all would exceed 20
      if (availableUsers.length > 20) {
        return;
      }
      setShareEmail(allSelected ? [] : availableUsers);
      return;
    }

    // ENFORCE LIMIT: Only update if the selection is 20 or fewer
    if (value.length <= 20) {
      setShareEmail(value);
    }
  };

  const handleRowClick = (freq: string) => {
    if (setFrequency) {
      setFrequency(freq);
    }
  };

  const handleDateStrategy = (value: 'current' | 'original') => {
    if (setDateStrategy) {
      setDateStrategy(value);
    }
  };

  // Determine which UI step to render based on logical variables
  const isFrequencyStepActive =
    needsFrequencyStep && step === frequencyStepNumber;
  const isDateRefreshStepActive =
    shouldShowDateRefreshStep && step === dateRefreshStepNumber;

  const wizardCopy = (() => {
    if (step === 2) {
      return {
        title: 'Share',
        description:
          'Choose whether to share this with your team. You can change this at any time.',
      };
    }
    if (step === 3 && teamOpt === 'yes') {
      return {
        title: 'Select team members',
        description: 'Select team members who should have access to this.',
      };
    }
    if (isFrequencyStepActive) {
      return {
        title: isScreening ? 'Screening frequency' : 'Update frequency',
        description: isScreening
          ? 'Choose how often this screen should automatically re-run.'
          : 'Choose how often we should check for updates.',
      };
    }
    if (isDateRefreshStepActive) {
      return {
        title: 'Tariff updates',
        description: 'Choose how recalculations should handle dates.',
      };
    }
    if (step === 3 && teamOpt === 'no' && acctCreating) {
      return {
        title: 'Setting up dashboard...',
        description:
          'We are creating your dashboard. Wait one moment while we set everything up!',
      };
    }
    return {
      title: 'Save work',
      description:
        'Create a descriptive name to help track and manage your request effectively.',
    };
  })();

  return (
    <div
      className='modal-cont ticket-wizard-modal'
      style={{ width: '480px', maxWidth: '90vw', padding: 0 }}
    >
      {/* --- Header --- */}
      <div
        className='modal-header-cont-temp'
        style={{
          padding: '24px',
          borderBottom: '1px solid var(--border-color)',
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        <div className='modal-header-cont-text'>
          <h1
            className='modal-header-cont-text-h1'
            style={{ fontSize: '20px', marginBottom: '4px' }}
          >
            {wizardCopy.title}
          </h1>
          <p className='modal-header-cont-text-p' style={{ margin: 0 }}>
            {wizardCopy.description}
          </p>
        </div>
      </div>

      {/* --- Body --- */}
      <div
        className='ticket-wizard-body'
        style={{
          padding: '24px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          gap: '16px',
          width: '100%',
          boxSizing: 'border-box',
        }}
      >

      {/* --- Step 1: Name --- */}
      {step === 1 && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            width: '100%',
          }}
        >
          <label
            style={{
              color: 'var(--text-primary-color)',
              fontSize: '14px',
              fontWeight: 500,
            }}
          >
            Name
          </label>
          <input
            id='searchVal'
            autoFocus
            placeholder='Enter name...'
            type='text'
            name='newTeamName'
            value={newTicketName}
            onChange={(e) => setNewTicketName(e.target.value)}
            style={{
              background: 'var(--bg-colors)',
              color: 'var(--text-primary-color)',
              border: '1px solid var(--border-color)',
              padding: '12px 14px',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              width: '100%',
              boxSizing: 'border-box',
            }}
          />
        </div>
      )}

      {/* --- Step 2: Share Graphic --- */}
      {step === 2 && (
        <div className='wizard-team-graphic-container'>
          <div className='team-graphic-frame'>
            <div
              className={`team-yes-select${
                teamOpt === 'yes' ? '-selected' : ''
              }`}
              onClick={() => handleTeamYesSelect()}
            >
              <div className='team-yes-frame'>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='88'
                  height='80'
                  viewBox='0 0 88 80'
                  fill='none'
                >
                  <g clipPath='url(#clip0_4181_61824)'>
                    <path
                      d='M69.8023 44.4666C68.8901 44.4925 68.0006 44.2665 67.2297 43.8124C64.8819 42.4288 61.4122 40.1437 58.291 37.1522C54.3305 33.3563 52.0546 29.4982 51.557 25.709L51.549 25.6497L51.4525 9.48285C51.4525 6.28778 53.5723 3.69428 57.5519 1.99014C60.8401 0.582354 65.1982 -0.115502 69.8023 0.0156127C74.3526 0.14495 78.5632 1.06808 81.6801 2.60628C85.3847 4.43477 87.3288 7.00411 87.3288 10.0503L87.2406 25.498L87.2334 25.5548C86.7784 29.183 84.6846 32.9335 80.984 36.7259C78.0157 39.7674 74.6617 42.1783 72.3583 43.6717C71.5961 44.166 70.7127 44.4413 69.8027 44.4669L69.8023 44.4666Z'
                      fill='#0765D3'
                    />
                    <path
                      d='M70.7052 44.4666C69.8053 44.4925 68.927 44.2665 68.1666 43.8124C65.8495 42.4288 62.4261 40.1437 59.346 37.1522C55.4376 33.3563 53.1921 29.4982 52.7009 25.709L52.6933 25.6497L52.5979 9.48285C52.5979 6.28778 54.6895 3.69428 58.6167 1.99014C61.8615 0.582354 66.1621 -0.115502 70.7052 0.0156127C75.1955 0.14495 79.3504 1.06808 82.4258 2.60628C86.0816 4.43477 87.9996 7.00411 87.9996 10.0503L87.9125 25.498L87.9057 25.5548C87.4568 29.183 85.3905 32.9335 81.7391 36.7259C78.8101 39.7674 75.5002 42.1783 73.2272 43.6717C72.4751 44.166 71.6033 44.4413 70.7052 44.4669V44.4666Z'
                      fill='#4895EF'
                    />
                    <path
                      d='M53.684 9.49458L53.7801 25.5797C54.8571 33.787 64.668 40.4315 68.9407 42.9561C70.1648 43.6792 71.7298 43.6365 72.9453 42.8502C77.1291 40.1437 86.4025 33.33 87.3932 25.4394L87.481 10.0343C87.481 -1.39502 53.684 -2.43967 53.684 9.49458Z'
                      fill='#A2C6F0'
                    />
                    <path
                      d='M70.9476 40.4767C70.8692 40.4784 70.7507 40.4656 70.631 40.3942C68.8716 39.3467 65.4612 37.153 62.4943 34.2876C59.2896 31.1928 57.4413 28.1839 57.0173 25.3576L56.9233 9.539C56.9273 8.02106 58.1493 6.72235 60.5448 5.68268C63.219 4.52219 67.0172 3.91352 70.9476 4.0052C74.8391 4.09616 78.526 4.86615 81.0805 6.11226C83.3401 7.21448 84.4822 8.5139 84.4858 9.97925L84.3984 25.2503C84.0026 27.9849 82.2681 30.9334 79.2221 34.0325C76.3581 36.9465 73.0128 39.2565 71.2642 40.3797C71.1446 40.4564 71.0261 40.4749 70.948 40.4767H70.9476Z'
                      fill='#97B5D9'
                    />
                    <path
                      d='M70.5461 39.3954C68.7693 38.3195 65.6098 36.2422 62.8532 33.5752C59.8415 30.6612 58.0987 27.8648 57.6888 25.2777L57.5956 9.5507C57.5985 8.46554 58.613 7.46992 60.6037 6.60364C63.1369 5.50072 66.7667 4.91977 70.5461 5.00327C74.289 5.08606 77.8168 5.81269 80.2413 6.99201C82.125 7.90839 83.0781 8.90792 83.0806 9.96607L82.9931 25.181C82.6086 27.6914 80.9648 30.437 78.0901 33.3567C75.421 36.0674 72.3149 38.247 70.5461 39.3957V39.3954Z'
                      fill='#DEE6F2'
                    />
                    <path
                      d='M78.5 26.75V25.25C78.5 23.8521 77.5439 22.6775 76.25 22.3445M73.625 13.4681C74.7244 13.9131 75.5 14.991 75.5 16.25C75.5 17.509 74.7244 18.5869 73.625 19.0319M74.75 26.75C74.75 25.3522 74.75 24.6533 74.5216 24.1019C74.2172 23.3669 73.6331 22.7828 72.8981 22.4784C72.3467 22.25 71.6478 22.25 70.25 22.25H68C66.6022 22.25 65.9033 22.25 65.3519 22.4784C64.6169 22.7828 64.0328 23.3669 63.7284 24.1019C63.5 24.6533 63.5 25.3522 63.5 26.75M72.125 16.25C72.125 17.9069 70.7819 19.25 69.125 19.25C67.4681 19.25 66.125 17.9069 66.125 16.25C66.125 14.5931 67.4681 13.25 69.125 13.25C70.7819 13.25 72.125 14.5931 72.125 16.25Z'
                      stroke='#4895EF'
                      strokeWidth='1.5'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                    <path
                      d='M15.4002 74.1091C15.4002 74.1091 12.9169 75.5336 13.6578 77.211C14.1034 78.2198 15.1902 78.5737 15.9861 78.6952C16.4863 78.7716 16.9482 78.4156 16.9995 77.9206L17.143 76.5334L15.4006 74.1091H15.4002Z'
                      fill='url(#paint0_linear_4181_61824)'
                    />
                    <path
                      d='M45.2147 60.0454C45.0647 61.4134 46.0742 65.3546 47.4653 65.5017L58.9271 64.0026L59.4696 59.0494L48.0049 57.8356C46.6138 57.6881 45.3644 58.6777 45.2144 60.0454H45.2147Z'
                      fill='var(--text-primary)'
                    />
                    <path
                      d='M67.306 50.619C66.2185 49.7534 64.6232 49.9183 63.7424 50.9875L56.4865 59.7981L60.4249 62.9331L67.6808 54.1225C68.5612 53.0533 68.3935 51.4849 67.306 50.619Z'
                      fill='var(--text-primary)'
                    />
                    <path
                      d='M59.4861 64.8596C61.4113 64.2255 62.4492 62.1771 61.8043 60.2843C61.1594 58.3916 59.0758 57.3712 57.1506 58.0052C55.2253 58.6393 54.1874 60.6877 54.8323 62.5804C55.4772 64.4732 57.5608 65.4936 59.4861 64.8596Z'
                      fill='#001332'
                    />
                    <path
                      d='M53.1483 49.6628C56.287 49.6628 58.8314 46.1475 58.8314 41.8112C58.8314 37.4749 56.287 33.9597 53.1483 33.9597C50.0097 33.9597 47.4653 37.4749 47.4653 41.8112C47.4653 46.1475 50.0097 49.6628 53.1483 49.6628Z'
                      fill='#A2C6F0'
                    />
                    <path
                      d='M35.7794 22.7858H25.0835C14.1161 22.7858 5.22546 31.5264 5.22546 42.3087C5.22546 48.0625 9.9695 52.7264 15.822 52.7264H45.0413C50.8934 52.7264 55.6378 48.0625 55.6378 42.3087C55.6378 31.5264 46.7472 22.7858 35.7798 22.7858H35.7794Z'
                      fill='var(--text-primary)'
                    />
                    <path
                      d='M34.993 80H26.1277C17.0375 80 9.66809 72.7553 9.66809 63.8182C9.66809 59.0495 13.6004 55.1835 18.451 55.1835H42.6696C47.5203 55.1835 51.4526 59.0495 51.4526 63.8182C51.4526 72.7553 44.0835 80 34.993 80Z'
                      fill='var(--text-primary)'
                    />
                    <path
                      d='M45.4883 48.5155H25.1565C22.401 48.5155 20.1406 46.439 20.1406 43.8778V39.9504C20.1406 37.3893 22.4013 35.3838 25.1565 35.4702L45.4883 36.108C47.9489 36.1851 49.9215 38.1763 49.9215 40.5566V44.2065C49.9215 46.5864 47.9489 48.5162 45.4883 48.5162V48.5155Z'
                      fill='#4895EF'
                    />
                    <path
                      d='M31.6708 41.7057C31.6708 43.4163 30.1896 44.7846 28.3478 44.7615C26.4894 44.7384 24.9703 43.3104 24.9703 41.5718C24.9703 39.8332 26.4894 38.4659 28.3478 38.5171C30.1896 38.5679 31.6708 39.9952 31.6708 41.7057Z'
                      fill='white'
                    />
                    <path
                      d='M45.4107 41.9804C45.4107 43.6334 44.0275 44.9562 42.3079 44.9349C40.5734 44.9132 39.1559 43.5346 39.1559 41.8553C39.1559 40.1761 40.573 38.8539 42.3079 38.9015C44.0275 38.9488 45.4107 40.3271 45.4107 41.98V41.9804Z'
                      fill='white'
                    />
                    <g opacity='0.3'>
                      <path
                        d='M37.8099 49.9883C34.7863 46.4955 36.1297 43.702 32.1768 38.9687C26.7374 32.456 20.3922 33.1929 18.9386 28.7755C18.536 27.5514 18.4897 25.8715 19.57 23.5491C11.2854 25.8985 5.22583 33.4075 5.22583 42.3087C5.22583 48.0625 9.96987 52.7265 15.8224 52.7265H41.2868C39.7616 51.9092 38.647 50.9541 37.8106 49.988L37.8099 49.9883Z'
                        fill='white'
                      />
                    </g>
                    <path
                      d='M8.08292 49.6081C11.2216 49.6081 13.7659 46.0929 13.7659 41.7566C13.7659 37.4202 11.2216 33.905 8.08292 33.905C4.94427 33.905 2.3999 37.4202 2.3999 41.7566C2.3999 46.0929 4.94427 49.6081 8.08292 49.6081Z'
                      fill='#A2C6F0'
                    />
                    <path
                      d='M3.58744 45.19C4.95995 45.19 6.07258 43.6528 6.07258 41.7565C6.07258 39.8603 4.95995 38.3231 3.58744 38.3231C2.21493 38.3231 1.10229 39.8603 1.10229 41.7565C1.10229 43.6528 2.21493 45.19 3.58744 45.19Z'
                      fill='#001332'
                    />
                    <g opacity='0.4'>
                      <path
                        d='M37.1764 71.0917C35.9602 65.4758 22.5062 65.6116 22.1404 61.9407C21.8585 59.1109 29.5829 56.3095 30.5536 55.9574C31.3838 55.6565 32.2079 55.4017 33.0236 55.1835H18.4514C13.6007 55.1835 9.66846 59.0495 9.66846 63.8182C9.66846 72.7553 17.0375 80 26.1277 80H34.993C35.8449 80 36.6816 79.9364 37.4984 79.8138C36.7268 78.8737 36.224 77.8454 36.1022 76.7232C35.8326 74.2356 37.6477 73.2681 37.1764 71.0917Z'
                        fill='#D1E3F4'
                      />
                    </g>
                    <path
                      d='M30.7787 57.3311C38.5116 57.3311 44.7803 56.585 44.7803 55.6646C44.7803 54.7443 38.5116 53.9982 30.7787 53.9982C23.0459 53.9982 16.7772 54.7443 16.7772 55.6646C16.7772 56.585 23.0459 57.3311 30.7787 57.3311Z'
                      fill='#A2C6F0'
                    />
                    <path
                      d='M2.80579 64.245L11.4756 59.0509C12.6961 58.3196 14.2835 58.6316 15.1245 59.7679C16.0816 61.0616 15.6909 62.8851 14.2842 63.6889L5.48935 68.7149L2.80579 64.2446V64.245Z'
                      fill='var(--text-primary)'
                    />
                    <path
                      d='M19.9238 70.9442L20.2928 73.0961C20.4048 73.7502 19.858 74.3265 19.1858 74.2722C17.7614 74.1574 15.6037 74.3091 14.7569 76.0744C14.5342 76.5388 14.4887 77.0668 14.6134 77.5653L14.8165 78.3765C14.8165 78.3765 10.8419 77.0892 12.7203 72.6626C14.1952 69.1864 17.7321 69.739 19.1829 70.1366C19.5685 70.2421 19.8569 70.5558 19.9234 70.9439L19.9238 70.9442Z'
                      fill='#001332'
                    />
                    <path
                      d='M4.58283 65.5405L13.2263 70.7769C14.4432 71.5142 14.8812 73.046 14.2328 74.2985C13.4948 75.724 11.6649 76.2098 10.2977 75.3424L1.74963 69.9205L4.58283 65.5405Z'
                      fill='var(--text-primary)'
                    />
                    <path
                      d='M3.67639 70.9144C5.70681 70.9144 7.35279 69.2962 7.35279 67.3C7.35279 65.3039 5.70681 63.6857 3.67639 63.6857C1.64598 63.6857 0 65.3039 0 67.3C0 69.2962 1.64598 70.9144 3.67639 70.9144Z'
                      fill='#001332'
                    />
                    <path
                      d='M66.4593 46.4273L63.9785 44.6716C63.9785 44.6716 63.1396 47.1937 63.4175 47.8763C63.6955 48.5589 66.3342 50.8802 66.3342 50.8802C66.3342 50.8802 66.1166 48.9199 66.7404 47.3963C66.8836 47.047 66.7701 46.6469 66.4593 46.4269V46.4273Z'
                      fill='#00326C'
                    />
                    <path
                      d='M63.9784 44.672C64.2065 44.672 64.3695 44.8902 64.2986 45.1033C63.926 46.2269 63.2447 49.2024 66.1954 49.8565C68.9458 50.4666 70.1009 48.9661 71.0616 46.6494C71.2011 46.3125 71.6015 46.1611 71.9355 46.3175L74.0794 47.3209C74.4373 47.4883 74.5601 47.9314 74.3353 48.2519C73.3302 49.6835 70.5097 52.9645 66.5427 51.6317C61.6985 50.004 63.2433 45.8247 63.6712 44.8685C63.725 44.7484 63.8454 44.6716 63.9788 44.6716L63.9784 44.672Z'
                      fill='#001332'
                    />
                  </g>
                  <defs>
                    <linearGradient
                      id='paint0_linear_4181_61824'
                      x1='13.5197'
                      y1='76.4077'
                      x2='17.1426'
                      y2='76.4077'
                      gradientUnits='userSpaceOnUse'
                    >
                      <stop stopColor='#444B8C' />
                      <stop offset='1' stopColor='#26264F' />
                    </linearGradient>
                    <clipPath id='clip0_4181_61824'>
                      <rect width='88' height='80' fill='white' />
                    </clipPath>
                  </defs>
                </svg>
              </div>
              <div className='wizard-team-option-text'>Yes</div>
            </div>
            <div
              className={`team-no-select${teamOpt === 'no' ? '-selected' : ''}`}
              onClick={() => handleTeamNoSelect()}
            >
              <div className='team-no-frame'>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='126'
                  height='82'
                  viewBox='0 0 126 82'
                  fill='none'
                >
                  <path
                    d='M34.4205 66.03C49.2089 64.5644 60.2943 54.0996 59.1803 42.6564C58.0662 31.2132 45.1747 23.1248 30.3863 24.5905C15.5978 26.0561 4.51246 36.5209 5.62648 47.9641C6.7405 59.4073 19.632 67.4957 34.4205 66.03Z'
                    fill='var(--text-primary)'
                  />
                  <path
                    d='M14.2384 41.5443L50.0769 37.9924C50.723 37.9283 51.2996 38.4055 51.3631 39.058L52.2967 48.6477C52.3966 49.6742 51.652 50.5893 50.6346 50.6901L16.1401 54.1089C15.1227 54.2097 14.2158 53.4583 14.1159 52.4318L13.1823 42.8421C13.1188 42.1902 13.5918 41.6084 14.2384 41.5443Z'
                    fill='#4895EF'
                  />
                  <path
                    d='M27.501 47.3475C27.7106 46.9721 27.8236 46.5618 27.7821 46.1355C27.6481 44.7544 26.0885 43.7791 24.3001 43.9562C22.511 44.1333 21.1701 45.3963 21.3042 46.7773C21.3456 47.2037 21.5353 47.5842 21.813 47.9114L27.501 47.3481V47.3475Z'
                    fill='white'
                  />
                  <path
                    d='M43.8147 45.7321C44.0242 45.3567 44.1373 44.9464 44.0958 44.5201C43.9618 43.139 42.4022 42.1637 40.6132 42.3408C38.8241 42.5178 37.4832 43.7808 37.6173 45.1619C37.6587 45.5882 37.8484 45.9687 38.1261 46.296L43.8141 45.7327L43.8147 45.7321Z'
                    fill='white'
                  />
                  <path
                    d='M35.1484 65.6782L34.9905 64.053C48.6711 62.6984 58.9276 51.2362 58.8475 37.3913L60.4656 37.3815C60.5059 44.402 57.9587 51.16 53.2919 56.4097C48.5961 61.6926 42.1522 64.9842 35.1478 65.6776L35.1484 65.6782Z'
                    fill='#001332'
                  />
                  <path
                    d='M6.51039 46.5762L4.44194 46.7762C4.27781 45.8919 4.15172 45.0072 4.06824 44.1447L4.51748 44.1C5.01784 37.9248 7.506 32.0717 11.6565 27.4007C16.3903 22.0737 22.8853 18.7551 29.9449 18.056C37.0078 17.3569 44.0208 19.3379 49.6917 23.6341C54.6635 27.4007 58.2324 32.6545 59.913 38.6142L60.3622 38.5695C60.4463 39.4314 60.4934 40.3248 60.5025 41.2239L58.4346 41.4336C55.8681 27.9577 43.7078 18.8084 30.1482 20.1511C16.5937 21.4932 6.43145 32.8533 6.51039 46.5756V46.5762Z'
                    fill='#001332'
                  />
                  <path
                    d='M9.42167 55.4493L3.26516 56.059C2.5558 56.1295 1.92368 55.6057 1.85439 54.89L0.00630016 35.8899C-0.0635569 35.1741 0.455543 34.5363 1.1649 34.4664L7.32141 33.8567C8.03077 33.7862 8.66289 34.31 8.73218 35.0257L10.5803 54.0258C10.6501 54.7415 10.131 55.3793 9.42167 55.4493Z'
                    fill='#001332'
                  />
                  <path
                    d='M34.5475 66.6828L33.6939 63.2325C33.5956 62.8348 33.8353 62.4319 34.2295 62.3328L44.6881 59.699C45.0823 59.5999 45.4816 59.8417 45.5798 60.2394L46.4334 63.6898C46.5317 64.0875 46.292 64.4903 45.8979 64.5895L35.4392 67.2232C35.045 67.3223 34.6458 67.0805 34.5475 66.6828Z'
                    fill='#001332'
                  />
                  <path
                    d='M64.11 50.034L57.9535 50.6437C57.2442 50.7142 56.612 50.1904 56.5427 49.4747L54.6947 30.4746C54.6248 29.7589 55.1439 29.1211 55.8533 29.0511L62.0098 28.4414C62.7191 28.3709 63.3512 28.8947 63.4205 29.6104L65.2686 48.6105C65.3385 49.3263 64.8194 49.9641 64.11 50.034Z'
                    fill='#001332'
                  />
                  <g opacity='0.3'>
                    <path
                      d='M48.7688 48.713C39.341 49.0242 28.7585 45.9229 25.3758 36.5071C24.1127 32.7192 24.6721 28.7514 26.4066 25.2208C13.6614 27.9921 4.61235 37.5747 5.62328 47.9652C6.73645 59.4085 19.6271 67.4977 34.4157 66.0335C45.1618 64.9693 53.9519 59.1529 57.5276 51.662C55.9425 51.7273 54.3142 51.4752 52.6746 50.8156C51.3927 50.3199 50.2148 48.7772 48.7688 48.713Z'
                      fill='#D8DEE8'
                    />
                  </g>
                  <path
                    d='M74.1882 33.0848C71.6205 29.8402 70.1416 26.0306 70.1416 21.955C70.1416 10.1061 82.646 0.5 98.0708 0.5C113.496 0.5 126 10.1061 126 21.9556C126 33.8051 113.496 43.4112 98.0708 43.4112C92.2897 43.4112 86.9192 42.0617 82.4631 39.7505C79.5178 40.2577 74.9458 41.1093 72.5332 41.5625C71.8227 41.6961 71.2406 40.9952 71.495 40.3127L74.1882 33.0854V33.0848Z'
                    fill='#4895EF'
                  />
                  <path
                    d='M97 25H94C92.1362 25 91.2044 25 90.4693 25.3045C89.4892 25.7105 88.7105 26.4892 88.3045 27.4693C88 28.2044 88 29.1362 88 31M101.5 13.2908C102.966 13.8841 104 15.3213 104 17M97.9999 31.5L100.025 31.095C100.202 31.0597 100.29 31.042 100.372 31.0097C100.445 30.9811 100.515 30.9439 100.579 30.899C100.652 30.8484 100.715 30.7848 100.843 30.6574L107.5 24C108.052 23.4477 108.052 22.5523 107.5 22C106.948 21.4477 106.052 21.4477 105.5 22L98.8425 28.6575C98.7152 28.7848 98.6516 28.8484 98.601 28.921C98.5561 28.9853 98.5189 29.0548 98.4902 29.1278C98.458 29.2102 98.4403 29.2984 98.405 29.475L97.9999 31.5ZM99.5 17C99.5 19.2091 97.7091 21 95.5 21C93.2909 21 91.5 19.2091 91.5 17C91.5 14.7909 93.2909 13 95.5 13C97.7091 13 99.5 14.7909 99.5 17Z'
                    stroke='var(--text-primary)'
                    strokeWidth='1.5'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                  <g opacity='0.3'>
                    <path
                      d='M82.7869 26.4959C89.0496 23.8398 95.9365 29.446 100.242 25.5355C104.582 21.5929 99.8928 13.7988 104.05 8.24365C106.032 5.595 109.362 4.34231 113.171 3.90393C120.888 7.7216 125.999 14.3799 125.999 21.955C125.999 33.8046 113.494 43.4106 98.0697 43.4106C92.2886 43.4106 86.9181 42.0611 82.462 39.75C80.138 40.15 76.8025 40.7649 74.3126 41.2285C76.1494 31.6213 79.5269 27.8775 82.7863 26.4953L82.7869 26.4959Z'
                      fill='#4895EF'
                    />
                  </g>
                  <path
                    d='M84.3863 74.6556C85.6749 72.7399 86.3337 70.5577 86.1594 68.2925C85.6522 61.7053 78.2894 56.91 69.7146 57.5821C61.1398 58.2543 54.5999 64.1396 55.1071 70.7268C55.6143 77.314 62.9771 82.1093 71.5519 81.4371C74.7659 81.185 77.6936 80.201 80.0716 78.722C81.7306 78.8756 84.3085 79.1495 85.6693 79.2968C86.0697 79.3398 86.3633 78.9249 86.1929 78.5564L84.3863 74.6562V74.6556Z'
                    fill='#A2C6F0'
                  />
                  <path
                    d='M70.5 67V72M68 69.5H73M67.875 75.125H73.125C74.1751 75.125 74.7001 75.125 75.1012 74.9206C75.454 74.7409 75.7409 74.454 75.9206 74.1012C76.125 73.7001 76.125 73.1751 76.125 72.125V66.875C76.125 65.8249 76.125 65.2999 75.9206 64.8988C75.7409 64.546 75.454 64.2591 75.1012 64.0794C74.7001 63.875 74.1751 63.875 73.125 63.875H67.875C66.8249 63.875 66.2999 63.875 65.8988 64.0794C65.546 64.2591 65.2591 64.546 65.0794 64.8988C64.875 65.2999 64.875 65.8249 64.875 66.875V72.125C64.875 73.1751 64.875 73.7001 65.0794 74.1012C65.2591 74.454 65.546 74.7409 65.8988 74.9206C66.2999 75.125 66.8249 75.125 67.875 75.125Z'
                    stroke='var(--text-primary)'
                    strokeWidth='1.5'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                  <g opacity='0.3'>
                    <path
                      d='M79.3242 71.3675C75.7292 70.1641 72.1409 73.5806 69.58 71.5944C66.9987 69.5922 69.2711 65.0547 66.7227 62.1477C65.5073 60.7615 63.603 60.2102 61.4664 60.1328C57.3397 62.5912 54.7834 66.5155 55.1077 70.7268C55.6149 77.314 62.9777 82.1093 71.5525 81.4371C74.7665 81.185 77.6942 80.2011 80.0722 78.722C81.3813 78.8429 83.2618 79.0395 84.6658 79.189C83.2334 73.9284 81.1956 71.9944 79.3242 71.3681V71.3675Z'
                      fill='#A2C6F0'
                    />
                  </g>
                </svg>
              </div>
              <div className='wizard-team-option-text'>No</div>
            </div>
          </div>
        </div>
      )}

      {/* --- Step 3: Team Members --- */}
      {step === 3 && teamOpt === 'yes' && (
        <div className='team-create-container'>
          <div className='team-name-form'>
            <div className='team-name-form-label'>Select Team Member</div>
            <FormControl sx={{ width: '100%' }}>
              <CustomSelect
                multiple
                value={shareEmail}
                onChange={handleShareChange}
                input={<OutlinedInput />}
                fullWidth
                IconComponent={KeyboardArrowDownIcon}
                renderValue={(selected: string[]) => selected.join(', ')}
                inputProps={{
                  MenuProps: {
                    MenuListProps: {
                      sx: {
                        backgroundColor: 'var(--bg-colors)',
                        color: 'var(--Text-Main, var(--text-primary))',
                      },
                    },
                  },
                }}
              >
                {/* <MenuItem value={SELECT_ALL_VALUE} disabled={availableUsers.length === 0}>
                  <Checkbox checked={allSelected} />
                  Select All
                </MenuItem> */}
                {filteredUserOpts.map((user: any, i: number) => (
                  <MenuItem key={i} value={user.label}>
                    <Checkbox checked={shareEmail.indexOf(user.label) > -1} />
                    {user.label}
                  </MenuItem>
                ))}
              </CustomSelect>
            </FormControl>
          </div>
        </div>
      )}

      {/* --- Step 4 (Dynamic): Frequency --- */}
      {isFrequencyStepActive && (
        <div className='team-create-container' style={{ marginBottom: '50px' }}>
          <div className='team-name-form'>
            <div className='team-name-form-label'>
              {isScreening ? 'Screen:' : 'Update:'}
            </div>
            <div className='audit-add-modal-cont-body-frame'>
              <div className='bill-container-fit-screen-content-row-selection'>
                {['Once', 'Daily', 'Weekly', 'Monthly', 'Yearly'].map((f) => (
                  <div
                    key={f}
                    className='bill-container-fit-screen-content-row-checkbox'
                    onClick={() => handleRowClick(f)}
                  >
                    <div className='bill-container-fit-screen-content-row-input'>
                      <div
                        className={`bill-container-fit-screen-content-row-input-icon ${
                          frequency === f ? 'selected' : ''
                        }`}
                      >
                        <div
                          className={`bill-container-fit-screen-content-row-input-icon-rnd ${
                            frequency === f ? 'selected' : ''
                          }`}
                        />
                      </div>
                    </div>
                    <div className='bill-container-fit-screen-content-row-checkbox-text'>
                      <p>{f}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* --- Step 5 (Dynamic): Date Strategy --- */}
      {isDateRefreshStepActive && (
        <div className='team-create-container' style={{ marginBottom: '50px' }}>
          <div className='team-name-form'>
            <div className='team-name-form-label'>Update Preferences</div>
            <div
              className='wizard-body-text'
              style={{ marginBottom: '16px', alignSelf: 'flex-start' }}
            >
              We can send you an update when the duty and/or tariff value
              changes
            </div>
            <div className='audit-add-modal-cont-body-frame'>
              <div className='bill-container-fit-screen-content-row-selection'>
                <div
                  className='bill-container-fit-screen-content-row-checkbox'
                  onClick={() => handleDateStrategy('current')}
                >
                  <div className='bill-container-fit-screen-content-row-input'>
                    <div
                      className={`bill-container-fit-screen-content-row-input-icon ${
                        dateStrategy === 'current' ? 'selected' : ''
                      }`}
                    >
                      <div
                        className={`bill-container-fit-screen-content-row-input-icon-rnd ${
                          dateStrategy === 'current' ? 'selected' : ''
                        }`}
                      />
                    </div>
                  </div>
                  <div className='bill-container-fit-screen-content-row-checkbox-text'>
                    <p>
                      Re-calculate the transaction using the current date each
                      time
                    </p>
                  </div>
                </div>
                <div
                  className='bill-container-fit-screen-content-row-checkbox'
                  onClick={() => handleDateStrategy('original')}
                >
                  <div className='bill-container-fit-screen-content-row-input'>
                    <div
                      className={`bill-container-fit-screen-content-row-input-icon ${
                        dateStrategy === 'original' ? 'selected' : ''
                      }`}
                    >
                      <div
                        className={`bill-container-fit-screen-content-row-input-icon-rnd ${
                          dateStrategy === 'original' ? 'selected' : ''
                        }`}
                      />
                    </div>
                  </div>
                  <div className='bill-container-fit-screen-content-row-checkbox-text'>
                    <p>
                      Re-calculate using the original loading and entry dates
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      </div>

      {/* --- Footer --- */}
      <div
        className='ticket-wizard-footer'
        style={{
          padding: '24px',
          borderTop: '1px solid var(--border-color)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        <div style={{ display: 'flex', gap: '6px' }}>
          {Array.from({ length: totalSteps }, (_, index) => (
            <span
              key={`wizard-dot-${index}`}
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '999px',
                background:
                  step === index + 1
                    ? 'var(--action-color)'
                    : 'var(--border-color)',
                transition: 'background 160ms ease',
              }}
            />
          ))}
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            type='button'
            className='btn-outline'
            disabled={acctCreating || step <= 1}
            onClick={() => handleStep('decrease')}
            style={{
              margin: 0,
              width: 'auto',
              opacity: acctCreating || step <= 1 ? 0.5 : 1,
              cursor: acctCreating || step <= 1 ? 'not-allowed' : 'pointer',
            }}
          >
            Back
          </button>
          {savingTicket ? (
            <button
              type='button'
              className='btn-primary'
              disabled
              style={{ margin: 0, width: 'auto', opacity: 0.6 }}
            >
              Loading...
            </button>
          ) : step < totalSteps ? (
            <button
              type='button'
              className='btn-primary'
              disabled={step === 1 && !newTicketName}
              onClick={() => {
                if (step === 1 && !newTicketName) return;
                handleStep('increase');
              }}
              style={{
                margin: 0,
                width: 'auto',
                opacity: step === 1 && !newTicketName ? 0.5 : 1,
                cursor:
                  step === 1 && !newTicketName ? 'not-allowed' : 'pointer',
              }}
            >
              Next
            </button>
          ) : (
            <button
              type='button'
              className='btn-primary'
              disabled={!newTicketName}
              onClick={() => newTicketName && handleSaveTicket(newTicketName)}
              style={{
                margin: 0,
                width: 'auto',
                opacity: !newTicketName ? 0.5 : 1,
                cursor: !newTicketName ? 'not-allowed' : 'pointer',
              }}
            >
              Submit
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateTicketWizard;
